/**
 * Version checker utility.
 * Periodically polls /version.json to detect when a new build has been deployed.
 * When a mismatch is detected, it notifies the app so a reload banner can be shown.
 */

let currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null
let checkInterval = null
let onUpdateCallback = null

/**
 * Fetch the deployed version from the server.
 * Returns null if the fetch fails (offline, 404, etc.)
 */
async function fetchDeployedVersion() {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const data = await res.json()
    return data.version || null
  } catch {
    return null
  }
}

/**
 * Start polling for version changes.
 * @param {Function} callback - Called with { currentVersion, deployedVersion } when an update is detected.
 * @param {number} intervalMs - How often to check (default: 5 minutes).
 */
export function startVersionCheck(callback, intervalMs = 5 * 60 * 1000) {
  if (!currentVersion) {
    // In dev mode, __APP_VERSION__ may not be defined — skip
    return
  }

  onUpdateCallback = callback

  // Don't start multiple intervals
  if (checkInterval) return

  checkInterval = setInterval(async () => {
    const deployedVersion = await fetchDeployedVersion()
    if (deployedVersion && deployedVersion !== currentVersion) {
      if (onUpdateCallback) {
        onUpdateCallback({ currentVersion, deployedVersion })
      }
      // Stop polling after detecting an update
      stopVersionCheck()
    }
  }, intervalMs)
}

/**
 * Stop the version polling interval.
 */
export function stopVersionCheck() {
  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }
}

/**
 * Get the current build version.
 */
export function getCurrentVersion() {
  return currentVersion
}
